// src/index.ts
import { useMemo as useMemo6 } from "react";

// src/useColumnFilters.ts
import { functionalUpdate } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

// src/encoder-decoder/noneStringForCustomDefaultValue.ts
var noneStringForCustomDefaultValue = "none";

// src/encoder-decoder/columnFilters.ts
var encodeColumnFilters = (stateValue, defaultValue) => {
  if (JSON.stringify(stateValue) === JSON.stringify(defaultValue)) {
    return void 0;
  }
  if (stateValue.length === 0) {
    return noneStringForCustomDefaultValue;
  }
  return stateValue.map(
    ({ id, value }) => `${id}.${encodeURIComponent(JSON.stringify(value)).replaceAll(".", "%2E")}`
  ).join(",");
};
var decodeColumnFilters = (queryValue, defaultValue) => {
  if (typeof queryValue !== "string") return defaultValue;
  if (queryValue === "") return defaultValue;
  if (queryValue === noneStringForCustomDefaultValue) {
    return [];
  }
  try {
    return queryValue.split(",").map((item) => {
      const [id, stringValue] = item.split(".");
      if (!id) throw new Error("Invalid columnFilters");
      if (stringValue === void 0) throw new Error("Invalid columnFilters");
      return {
        id,
        value: stringValue === "undefined" ? void 0 : JSON.parse(decodeURIComponent(stringValue))
      };
    }).filter((x) => x !== null);
  } catch {
    return defaultValue;
  }
};

// src/convertQueryToURLSearchParams.ts
function convertQueryToURLSearchParams(query) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (!value) continue;
    if (Array.isArray(value)) {
      for (const element of value) {
        searchParams.append(key, element);
      }
    } else {
      searchParams.append(key, value);
    }
  }
  return searchParams;
}

// src/updateQuery.ts
var updateQuery = async ({ oldQuery, newQuery, router }) => {
  const oldQueryEntries = Object.entries(oldQuery);
  const newQueryEntries = Object.entries(newQuery);
  if (oldQueryEntries.length === newQueryEntries.length && oldQueryEntries.every(([key, value]) => newQuery[key] === value)) {
    return;
  }
  const oldQueryParamNames = Object.keys(oldQuery);
  const unchangedQuery = Object.fromEntries(
    Object.entries(router.query).filter(
      ([key]) => !oldQueryParamNames.includes(key)
    )
  );
  const newQueryExcludedUndefined = Object.fromEntries(
    Object.entries(newQuery).filter(([, value]) => value !== void 0)
  );
  const searchParams = new URLSearchParams(
    convertQueryToURLSearchParams({
      ...unchangedQuery,
      ...newQueryExcludedUndefined
    })
  ).toString();
  await router.navigate(
    `${router.pathname}${searchParams ? `?${searchParams}` : ""}`
  );
};

// src/useDebounce.ts
import { useEffect, useState } from "react";
var useDebounce = ({
  stateValue,
  milliseconds,
  updateQuery: updateQuery2
}) => {
  const [debouncedValue, setDebouncedValue] = useState(stateValue);
  useEffect(() => {
    if (milliseconds === void 0) return;
    const handler = setTimeout(() => updateQuery2(debouncedValue), milliseconds);
    return () => clearTimeout(handler);
  }, [debouncedValue, milliseconds, updateQuery2]);
  useEffect(() => {
    if (milliseconds === void 0) return;
    setDebouncedValue(stateValue);
  }, [stateValue, milliseconds]);
  return [debouncedValue, setDebouncedValue];
};

// src/useColumnFilters.ts
var defaultDefaultColumnFilters = [];
var useColumnFilters = ({ router, options }) => {
  const paramName = (typeof options?.paramName === "function" ? options?.paramName(PARAM_NAMES.COLUMN_FILTERS) : options?.paramName) || PARAM_NAMES.COLUMN_FILTERS;
  const stringDefaultColumnFilters = JSON.stringify(
    options?.defaultValue ?? defaultDefaultColumnFilters
  );
  const uncustomisedColumnFilters = useMemo(
    () => decodeColumnFilters(
      router.query[paramName],
      JSON.parse(stringDefaultColumnFilters)
    ),
    [router.query[paramName], paramName, stringDefaultColumnFilters]
  );
  const isCustomDecoder = !!options?.decoder;
  const stringCustomColumnFilters = options?.decoder?.(router.query) ? JSON.stringify(options.decoder(router.query)) : "";
  const _columnFilters = useMemo(
    () => isCustomDecoder ? stringCustomColumnFilters === "" ? [] : JSON.parse(stringCustomColumnFilters) : uncustomisedColumnFilters,
    [stringCustomColumnFilters, uncustomisedColumnFilters, isCustomDecoder]
  );
  const updateColumnFiltersQuery = useCallback(
    async (newColumnFilters) => {
      const encoder = (columnFilters2) => options?.encoder ? options.encoder(columnFilters2) : {
        [paramName]: encodeColumnFilters(
          columnFilters2,
          JSON.parse(stringDefaultColumnFilters)
        )
      };
      await updateQuery({
        oldQuery: encoder(_columnFilters),
        newQuery: encoder(newColumnFilters),
        router
      });
    },
    [
      router,
      paramName,
      options?.encoder,
      stringDefaultColumnFilters,
      _columnFilters
    ]
  );
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useDebounce({
    stateValue: _columnFilters,
    updateQuery: updateColumnFiltersQuery,
    milliseconds: options?.debounceMilliseconds
  });
  const columnFilters = useMemo(
    () => options?.debounceMilliseconds === void 0 ? _columnFilters : debouncedColumnFilters,
    [_columnFilters, debouncedColumnFilters, options?.debounceMilliseconds]
  );
  return {
    columnFilters,
    onColumnFiltersChange: useCallback(
      async (updater) => {
        const newColumnFilters = functionalUpdate(updater, columnFilters);
        if (options?.debounceMilliseconds !== void 0) {
          setDebouncedColumnFilters(newColumnFilters);
          return;
        }
        await updateColumnFiltersQuery(newColumnFilters);
      },
      [
        columnFilters,
        updateColumnFiltersQuery,
        options?.debounceMilliseconds,
        setDebouncedColumnFilters
      ]
    )
  };
};

// src/useColumnOrder.ts
import { functionalUpdate as functionalUpdate2 } from "@tanstack/react-table";
import { useCallback as useCallback2, useMemo as useMemo2 } from "react";

// src/encoder-decoder/columnOrder.ts
var encodeColumnOrder = (stateValue, defaultValue) => {
  if (JSON.stringify(stateValue) === JSON.stringify(defaultValue)) {
    return void 0;
  }
  if (stateValue.length === 0) {
    return noneStringForCustomDefaultValue;
  }
  return stateValue.map((v) => v.replaceAll(",", encodeURIComponent(","))).join(",");
};
var decodeColumnOrder = (queryValue, defaultValue) => {
  if (typeof queryValue !== "string") return defaultValue;
  if (queryValue === "") return defaultValue;
  if (queryValue === noneStringForCustomDefaultValue) {
    return [];
  }
  return queryValue.split(",").map((v) => v.replaceAll(encodeURIComponent(","), ","));
};

// src/useColumnOrder.ts
var defaultDefaultColumnOrder = [];
var useColumnOrder = ({ router, options }) => {
  const paramName = (typeof options?.paramName === "function" ? options?.paramName(PARAM_NAMES.COLUMN_ORDER) : options?.paramName) || PARAM_NAMES.COLUMN_ORDER;
  const stringDefaultColumnOrder = JSON.stringify(
    options?.defaultValue ?? defaultDefaultColumnOrder
  );
  const uncustomisedColumnOrder = useMemo2(
    () => decodeColumnOrder(
      router.query[paramName],
      JSON.parse(stringDefaultColumnOrder)
    ),
    [router.query[paramName], paramName, stringDefaultColumnOrder]
  );
  const isCustomDecoder = !!options?.decoder;
  const stringCustomColumnOrder = options?.decoder?.(router.query) ? JSON.stringify(options.decoder(router.query)) : "";
  const _columnOrder = useMemo2(
    () => isCustomDecoder ? stringCustomColumnOrder === "" ? [] : JSON.parse(stringCustomColumnOrder) : uncustomisedColumnOrder,
    [stringCustomColumnOrder, uncustomisedColumnOrder, isCustomDecoder]
  );
  const updateColumnOrderQuery = useCallback2(
    async (newColumnOrder) => {
      const encoder = (columnOrder2) => options?.encoder ? options.encoder(columnOrder2) : {
        [paramName]: encodeColumnOrder(
          columnOrder2,
          JSON.parse(stringDefaultColumnOrder)
        )
      };
      await updateQuery({
        oldQuery: encoder(_columnOrder),
        newQuery: encoder(newColumnOrder),
        router
      });
    },
    [
      router,
      paramName,
      options?.encoder,
      stringDefaultColumnOrder,
      _columnOrder
    ]
  );
  const [debouncedColumnOrder, setDebouncedColumnOrder] = useDebounce({
    stateValue: _columnOrder,
    updateQuery: updateColumnOrderQuery,
    milliseconds: options?.debounceMilliseconds
  });
  const columnOrder = useMemo2(
    () => options?.debounceMilliseconds === void 0 ? _columnOrder : debouncedColumnOrder,
    [_columnOrder, debouncedColumnOrder, options?.debounceMilliseconds]
  );
  return {
    columnOrder,
    onColumnOrderChange: useCallback2(
      async (updater) => {
        const newColumnOrder = functionalUpdate2(updater, columnOrder);
        if (options?.debounceMilliseconds !== void 0) {
          setDebouncedColumnOrder(newColumnOrder);
          return;
        }
        await updateColumnOrderQuery(newColumnOrder);
      },
      [
        columnOrder,
        updateColumnOrderQuery,
        options?.debounceMilliseconds,
        setDebouncedColumnOrder
      ]
    )
  };
};

// src/useGlobalFilter.ts
import { functionalUpdate as functionalUpdate3 } from "@tanstack/react-table";
import { useCallback as useCallback3, useMemo as useMemo3 } from "react";

// src/encoder-decoder/globalFilter.ts
var encodedEmptyStringForGlobalFilterCustomDefaultValue = encodeURIComponent(JSON.stringify(""));
var encodeGlobalFilter = (stateValue, defaultValue) => {
  if (stateValue === defaultValue) {
    return void 0;
  }
  if (typeof stateValue !== "string") {
    return defaultValue;
  }
  if (stateValue === "") {
    return encodedEmptyStringForGlobalFilterCustomDefaultValue;
  }
  return stateValue;
};
var decodeGlobalFilter = (queryValue, defaultValue) => {
  if (typeof queryValue !== "string") {
    return defaultValue;
  }
  if (queryValue === encodedEmptyStringForGlobalFilterCustomDefaultValue) {
    return defaultValue === "" ? encodedEmptyStringForGlobalFilterCustomDefaultValue : "";
  }
  return queryValue;
};

// src/useGlobalFilter.ts
var defaultDefaultGlobalFilter = "";
var useGlobalFilter = ({ router, options }) => {
  const paramNames = (typeof options?.paramName === "function" ? options?.paramName(PARAM_NAMES.GLOBAL_FILTER) : options?.paramName) || PARAM_NAMES.GLOBAL_FILTER;
  const defaultGlobalFilter = options?.defaultValue ?? defaultDefaultGlobalFilter;
  const _globalFilter = options?.decoder ? options?.decoder?.(router.query) : decodeGlobalFilter(router.query[paramNames], defaultGlobalFilter);
  const updateGlobalFilterQuery = useCallback3(
    async (newGlobalFilter) => {
      const encoder = (globalFilter2) => options?.encoder ? options.encoder(globalFilter2) : {
        [paramNames]: encodeGlobalFilter(
          globalFilter2,
          defaultGlobalFilter
        )
      };
      await updateQuery({
        oldQuery: encoder(_globalFilter),
        newQuery: encoder(newGlobalFilter),
        router
      });
    },
    [router, paramNames, options?.encoder, defaultGlobalFilter, _globalFilter]
  );
  const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useDebounce({
    stateValue: _globalFilter,
    updateQuery: updateGlobalFilterQuery,
    milliseconds: options?.debounceMilliseconds
  });
  const globalFilter = useMemo3(
    () => options?.debounceMilliseconds === void 0 ? _globalFilter : debouncedGlobalFilter,
    [_globalFilter, debouncedGlobalFilter, options?.debounceMilliseconds]
  );
  return {
    globalFilter,
    onGlobalFilterChange: useCallback3(
      async (updater) => {
        const newGlobalFilter = functionalUpdate3(updater, globalFilter);
        if (options?.debounceMilliseconds !== void 0) {
          setDebouncedGlobalFilter(newGlobalFilter);
          return;
        }
        await updateGlobalFilterQuery(newGlobalFilter);
      },
      [
        globalFilter,
        options?.debounceMilliseconds,
        updateGlobalFilterQuery,
        setDebouncedGlobalFilter
      ]
    )
  };
};

// src/usePagination.ts
import { functionalUpdate as functionalUpdate4 } from "@tanstack/react-table";
import { useCallback as useCallback4, useMemo as useMemo4 } from "react";

// src/encoder-decoder/pagination.ts
var encodePagination = (stateValue, defaultValue) => ({
  pageIndex: stateValue.pageIndex === defaultValue.pageIndex ? void 0 : (stateValue.pageIndex + 1).toString(),
  pageSize: stateValue.pageSize === defaultValue.pageSize ? void 0 : stateValue.pageSize.toString()
});
var decodePagination = (queryValues, defaultValue) => {
  const pageIndex = Number(queryValues.pageIndex);
  const pageSize = Number(queryValues.pageSize);
  return {
    pageIndex: queryValues.pageIndex === "" || Number.isNaN(pageIndex) || pageIndex < 1 ? defaultValue.pageIndex : pageIndex - 1,
    pageSize: queryValues.pageSize === "" || Number.isNaN(pageSize) ? defaultValue.pageSize : pageSize
  };
};

// src/usePagination.ts
var defaultDefaultPagination = {
  pageIndex: 0,
  pageSize: 10
};
var usePagination = ({ router, options }) => {
  const paramName = useMemo4(
    () => (typeof options?.paramName === "function" ? options?.paramName({
      pageIndex: PARAM_NAMES.PAGE_INDEX,
      pageSize: PARAM_NAMES.PAGE_SIZE
    }) : options?.paramName) || {
      pageIndex: PARAM_NAMES.PAGE_INDEX,
      pageSize: PARAM_NAMES.PAGE_SIZE
    },
    [options?.paramName]
  );
  const defaultPagination = useMemo4(
    () => ({
      pageIndex: options?.defaultValue?.pageIndex ?? defaultDefaultPagination.pageIndex,
      pageSize: options?.defaultValue?.pageSize ?? defaultDefaultPagination.pageSize
    }),
    [options?.defaultValue?.pageIndex, options?.defaultValue?.pageSize]
  );
  const uncustomisedPagination = useMemo4(
    () => decodePagination(
      {
        pageIndex: router.query[paramName.pageIndex],
        pageSize: router.query[paramName.pageSize]
      },
      {
        pageIndex: defaultPagination.pageIndex,
        pageSize: defaultPagination.pageSize
      }
    ),
    [
      router.query[paramName.pageIndex],
      router.query[paramName.pageSize],
      paramName.pageIndex,
      paramName.pageSize,
      defaultPagination.pageIndex,
      defaultPagination.pageSize
    ]
  );
  const isCustomDecoder = !!options?.decoder;
  const stringCustomPagination = options?.decoder?.(router.query) ? JSON.stringify(options.decoder(router.query)) : "";
  const _pagination = useMemo4(
    () => isCustomDecoder ? stringCustomPagination === "" ? [] : JSON.parse(stringCustomPagination) : {
      pageIndex: uncustomisedPagination.pageIndex,
      pageSize: uncustomisedPagination.pageSize
    },
    [
      stringCustomPagination,
      isCustomDecoder,
      uncustomisedPagination.pageIndex,
      uncustomisedPagination.pageSize
    ]
  );
  const updatePaginationQuery = useCallback4(
    async (newPagination) => {
      const encoder = (pagination2) => {
        if (options?.encoder) return options.encoder(pagination2);
        const encoded = encodePagination(pagination2, {
          pageIndex: defaultPagination.pageIndex,
          pageSize: defaultPagination.pageSize
        });
        return {
          [paramName.pageIndex]: encoded.pageIndex,
          [paramName.pageSize]: encoded.pageSize
        };
      };
      await updateQuery({
        oldQuery: encoder(_pagination),
        newQuery: encoder(newPagination),
        router
      });
    },
    [
      router,
      _pagination,
      paramName,
      options?.encoder,
      defaultPagination.pageIndex,
      defaultPagination.pageSize
    ]
  );
  const [debouncedPagination, setDebouncedPagination] = useDebounce({
    stateValue: _pagination,
    updateQuery: updatePaginationQuery,
    milliseconds: options?.debounceMilliseconds
  });
  const pagination = useMemo4(
    () => options?.debounceMilliseconds === void 0 ? _pagination : debouncedPagination,
    [_pagination, debouncedPagination, options?.debounceMilliseconds]
  );
  return {
    pagination,
    onPaginationChange: useCallback4(
      async (updater) => {
        const newPagination = functionalUpdate4(updater, pagination);
        if (options?.debounceMilliseconds !== void 0) {
          setDebouncedPagination(newPagination);
          return;
        }
        await updatePaginationQuery(newPagination);
      },
      [
        pagination,
        updatePaginationQuery,
        options?.debounceMilliseconds,
        setDebouncedPagination
      ]
    )
  };
};

// src/useSorting.ts
import { functionalUpdate as functionalUpdate5 } from "@tanstack/react-table";
import { useCallback as useCallback5, useMemo as useMemo5 } from "react";

// src/encoder-decoder/sorting.ts
var encodeSorting = (stateValue, defaultValue) => {
  if (JSON.stringify(stateValue) === JSON.stringify(defaultValue)) {
    return void 0;
  }
  if (stateValue.length === 0) {
    return noneStringForCustomDefaultValue;
  }
  return stateValue.map(({ id, desc }) => `${id}.${desc ? "desc" : "asc"}`).join(",");
};
var decodeSorting = (queryValue, defaultValue) => {
  if (typeof queryValue !== "string") return defaultValue;
  if (queryValue === "") return defaultValue;
  if (queryValue === noneStringForCustomDefaultValue) {
    return [];
  }
  try {
    return queryValue.split(",").map((sort) => {
      const [id, order] = sort.split(".");
      if (!id) throw new Error("Invalid sorting");
      if (order !== "asc" && order !== "desc") {
        throw new Error("Invalid sorting");
      }
      return { id, desc: order === "desc" };
    });
  } catch {
    return defaultValue;
  }
};

// src/useSorting.ts
var defaultDefaultSorting = [];
var useSorting = ({ router, options }) => {
  const paramName = (typeof options?.paramName === "function" ? options?.paramName(PARAM_NAMES.SORTING) : options?.paramName) || PARAM_NAMES.SORTING;
  const stringDefaultSorting = JSON.stringify(
    options?.defaultValue ?? defaultDefaultSorting
  );
  const uncustomisedSorting = useMemo5(
    () => decodeSorting(router.query[paramName], JSON.parse(stringDefaultSorting)),
    [router.query[paramName], paramName, stringDefaultSorting]
  );
  const isCustomDecoder = !!options?.decoder;
  const stringCustomSorting = options?.decoder?.(router.query) ? JSON.stringify(options.decoder(router.query)) : "";
  const _sorting = useMemo5(
    () => isCustomDecoder ? stringCustomSorting === "" ? [] : JSON.parse(stringCustomSorting) : uncustomisedSorting,
    [stringCustomSorting, uncustomisedSorting, isCustomDecoder]
  );
  const updateSortingQuery = useCallback5(
    async (newSorting) => {
      const encoder = (sorting2) => options?.encoder ? options.encoder(sorting2) : {
        [paramName]: encodeSorting(
          sorting2,
          JSON.parse(stringDefaultSorting)
        )
      };
      await updateQuery({
        oldQuery: encoder(_sorting),
        newQuery: encoder(newSorting),
        router
      });
    },
    [router, paramName, options?.encoder, stringDefaultSorting, _sorting]
  );
  const [debouncedSorting, setDebouncedSorting] = useDebounce({
    stateValue: _sorting,
    updateQuery: updateSortingQuery,
    milliseconds: options?.debounceMilliseconds
  });
  const sorting = useMemo5(
    () => options?.debounceMilliseconds === void 0 ? _sorting : debouncedSorting,
    [_sorting, debouncedSorting, options?.debounceMilliseconds]
  );
  return {
    sorting,
    onSortingChange: useCallback5(
      async (updater) => {
        const newSorting = functionalUpdate5(updater, sorting);
        if (options?.debounceMilliseconds !== void 0) {
          setDebouncedSorting(newSorting);
          return;
        }
        await updateSortingQuery(newSorting);
      },
      [
        sorting,
        options?.debounceMilliseconds,
        updateSortingQuery,
        setDebouncedSorting
      ]
    )
  };
};

// src/index.ts
var PARAM_NAMES = {
  GLOBAL_FILTER: "globalFilter",
  SORTING: "sorting",
  PAGE_INDEX: "pageIndex",
  PAGE_SIZE: "pageSize",
  COLUMN_FILTERS: "columnFilters",
  COLUMN_ORDER: "columnOrder"
};
var regex = /s$/;
var extractSpecificStateOptions = ({
  options,
  key
}) => Object.fromEntries(
  options ? Object.entries(options).map(([k, v]) => [
    k === "debounceMilliseconds" ? k : k.replace(regex, ""),
    typeof v === "object" ? v?.[key] : v
  ]) : []
);
var useTableSearchParams = ({ pathname, query, ...props }, options) => {
  const useReplace = "replace" in props;
  const navigate = useReplace ? props.replace : props.push;
  const router = useMemo6(
    () => ({
      navigate,
      pathname,
      query: query instanceof URLSearchParams ? Object.fromEntries(query.entries()) : query
    }),
    [pathname, query, navigate]
  );
  const { globalFilter, onGlobalFilterChange } = useGlobalFilter({
    router,
    options: extractSpecificStateOptions({ options, key: "globalFilter" })
  });
  const { sorting, onSortingChange } = useSorting({
    router,
    options: extractSpecificStateOptions({ options, key: "sorting" })
  });
  const { pagination, onPaginationChange } = usePagination({
    router,
    options: extractSpecificStateOptions({ options, key: "pagination" })
  });
  const { columnFilters, onColumnFiltersChange } = useColumnFilters({
    router,
    options: extractSpecificStateOptions({ options, key: "columnFilters" })
  });
  const { columnOrder, onColumnOrderChange } = useColumnOrder({
    router,
    options: extractSpecificStateOptions({ options, key: "columnOrder" })
  });
  const state = useMemo6(
    () => ({ sorting, pagination, globalFilter, columnFilters, columnOrder }),
    [sorting, pagination, globalFilter, columnFilters, columnOrder]
  );
  return {
    state,
    onGlobalFilterChange,
    onSortingChange,
    onPaginationChange,
    onColumnFiltersChange,
    onColumnOrderChange
  };
};
export {
  PARAM_NAMES,
  useTableSearchParams
};
